import { EarlyAccessFeatures, MultiFactorType } from 'app/core/identity/user-identity';
import { SiteRole } from 'app/core/identity/site-role';
import { Account, AccountType, SourceCoreModel } from 'app/shared/account/account.model';
import { Tier } from 'app/shared/account/tier.model';
import { Currency } from '../shared/account/Currency';
import { WidgetDeviceView } from 'app/campaign/widgets.model';
import { SubscriptionProvider } from 'app/subscription/subscription.model';

// Export here for backwards compabillity, remove this when all other files are using the import above instead of this
export type { Account };
export { AccountType };

export interface FilterString {
  text: string;
  category: string;
  displayExp?: string;
  operator?: number;
}

/**
 * Extended Account with extra fields returned by the admin search API
 * (POST admin/accounts/v2/search). The backend enriches Account with these
 * properties; the shared Account interface does not include them.
 */
export interface AccountSearchResult extends Account {
  customerName?: string;
  /** createdBy resolved to an email by the server. What the cell renders. */
  createdByEmail?: string;
  revenue?: number;
  usage?: number;
  currentVisitors?: number;
  publishedCampaigns?: number;
  unpublishedCampaigns?: number;
  hasVisits?: boolean;
}

/** Paging and sorting, shared by all three backoffice search endpoints. */
export interface ServerPagedRequest {
  skip?: number;
  take?: number;
  orderBy?: string;
  orderDirection?: 'Ascending' | 'Descending';
  /** Refetches the server's cached enrichment datasets. Set on the reload right after a mutation. */
  refresh?: boolean;
}

/** Distinct filter value. The id is what the row carries; the email is what the user reads. */
export interface CreatedByOption {
  id: string;
  email: string;
}

export interface SearchCustomersRequest extends ServerPagedRequest {
  search?: string;
  name?: string;
  types?: (string | null)[];
  tier?: string;
  subscriptionProvider?: SubscriptionProvider;
  /** PLATFORM_FILTER_NONE matches customers running no e-commerce platform. */
  platform?: string;
  status?: 'Live' | 'Inactive';
  isPaused?: boolean;
  willBeDeleted?: boolean;
  pageViewsOveruse?: boolean;
  createdAfter?: string;
  createdBefore?: string;
  createdBy?: string;
  source?: string;
  /** A CSM's user id, ACCOUNT_MANAGER_FILTER_ME or ACCOUNT_MANAGER_FILTER_NONE. Matches the customer's CSM and the CSM of each of its accounts. */
  accountManager?: string;
}

/** The signed-in admin, resolved by the Gateway. */
export const ACCOUNT_MANAGER_FILTER_ME = 'Me';
export const ACCOUNT_MANAGER_FILTER_NONE = 'None';

/** A user with the CSM role, who can be picked as the CSM of a customer or an account. */
export interface CustomerSuccessManager {
  userId: string;
  fullName: string;
  email: string;
}

export interface CustomersSearchStats {
  totalCustomers: number;
  /** Customers before any filter, so the bar can show how much the filters narrowed. */
  totalUnfiltered: number;
  activeCustomers: number;
  selfServiceCustomers: number;
  invoiceCustomers: number;
  pageViewsOveruseCustomers: number;
}

export interface CustomersSearchResponse {
  customers: CustomerMock[];
  /** Number of customers matching the filters, not the size of the returned page. */
  total: number;
  stats: CustomersSearchStats;
  /** Computed over the unfiltered set, so a dropdown never shrinks as the user narrows the grid. */
  options: { createdBy: CreatedByOption[] };
}

export type AccountExpiryFilter = 'IncludeExpired' | 'ExcludeExpired' | 'OnlyExpired';
export type AccountStatusFilter = 'Paused' | 'Expired' | 'Inactive' | 'Active';

export interface SearchAccountsRequest extends ServerPagedRequest {
  search?: string;
  name?: string;
  ipDb?: string;
  type?: string;
  customerName?: string;
  customerId?: string;
  organizationsEnabled?: boolean;
  organizationsDisabled?: boolean;
  /** Tri-state. Omitted excludes expired accounts. */
  expiry?: AccountExpiryFilter;
  status?: AccountStatusFilter;
  hasTracking?: boolean;
  hasRevenue?: boolean;
  useSpaTracking?: boolean;
  createdAfter?: string;
  createdBefore?: string;
  createdBy?: string;
  source?: string;
}

export interface AccountsSearchStats {
  totalAccounts: number;
  /** Accounts before any filter, so the bar can show how much the filters narrowed. */
  totalUnfiltered: number;
  activeAccounts: number;
  pausedAccounts: number;
  expiredAccounts: number;
  invoiceAccounts: number;
  selfServiceAccounts: number;
}

export interface AccountsSearchResponse {
  accounts: AccountSearchResult[];
  total: number;
  stats: AccountsSearchStats;
  options: { createdBy: CreatedByOption[]; customerNames: string[] };
}

export interface SearchUsersRequest extends ServerPagedRequest {
  search?: string;
  language?: string;
  timeZoneId?: string;
  isAdmin?: boolean;
  isSiteAdmin?: boolean;
  isEnabled?: boolean;
  isVerified?: boolean;
  hasMultiFactorAuthentication?: boolean;
  hasEarlyAccess?: boolean;
  earlyAccessFeature?: string;
  siteId?: number;
  source?: string;
  createdBy?: string;
  createdAfter?: string;
  lastLoggedInAfter?: string;
  /** lastLoggedInAt is 0 for users who never logged in, so a "before" bound already includes them. */
  lastLoggedInBefore?: string;
}

export interface UsersSearchStats {
  totalUsers: number;
  /** Users before any filter, so the bar can show how much the filters narrowed. */
  totalUnfiltered: number;
  activeUsers: number;
  newThisWeek: number;
  inactiveUsers: number;
  earlyAccessUsers: number;
}

export interface UsersSearchResponse {
  users: UsersAdmin[];
  total: number;
  stats: UsersSearchStats;
  options: { createdBy: CreatedByOption[]; languages: string[]; timeZones: string[] };
}

export interface CustomerMock {
  id: string;
  createdTime: string;
  updatedTime: string;
  name: string;
  siteIds?: Array<number>;
  group?: string;
  includeGroup?: string;
  accounts?: Array<AccountSearchResult>;
  accountManagerUserId?: string | null;
  /** The CSM's name, resolved by admin/customers/search. */
  accountManagerName?: string | null;
  deleteAction?: null | string;
  isPaused?: boolean;
  type?: string;
  shouldBeDeleted?: boolean;
  // Fields returned by POST admin/customers/search
  pageViewsInPlan?: number;
  usedPageViews?: number;
  usedSessions?: number;
  extraSurveyResponsesInPlan?: number;
  usedSurveyResponses?: number;
  stripeCustomerId?: string;
  unpublishedCampaigns?: number;
  publishedCampaigns?: number;
  appsActivated?: number;
  /**
   * Count of distinct strategic objectives that have at least one published
   * campaign, aggregated across the customer's accounts. Enriched by
   * admin/customers/search. Pending backend support: until it ships this is
   * undefined and callers should treat it as 0.
   */
  objectivesInUse?: number;
  amountAccount?: number;
  mrr?: number;
  tier?: string;
  isActive?: boolean;
  source?: SourceCoreModel;
  /** Raw value the row carries — a user id or an email. What the createdBy filter sends back. */
  createdBy?: string;
  /** createdBy resolved to an email by the server. What the cell renders. */
  createdByEmail?: string;
  /** Always set by admin/customers/search; the null-means-Stripe default is applied server-side. */
  subscriptionProvider?: SubscriptionProvider;
  platforms?: EcommercePlatform[];
}

export interface AccountCustomers {
  name: string;
  id?: string;
  resetDayInMonth: number;
  eventsPerMonth: number;
  emailsPerMonth?: number;
  importExportRowsPerMonth: number;
  surveyResponsesPerMonth: number;
  /** Extra AI credits per month on top of the tier's included credits. */
  aiChatMessagesPerMonth?: number;
  users: number;
  accounts: number;
  type: string;
  peAccountingId: number;
  stripeCustomerId: number;
  siteIds?: number[];
  clientScript: string;
  ownerEmail?: string;
  tier?: Tier;
  daysToKeepUnmodifiedVisitors?: number;
  defaultEditorView?: WidgetDeviceView;
  subscriptionProvider?: SubscriptionProvider;
  accountManagerUserId?: string | null;
  /** Unlocks the native app product: App Experiences, allowed native apps and the Public API setting. */
  enableAppExperiences?: boolean;

  /** Gateway request limits. Undefined means the gateway's configured default applies. */
  publicApiCallsPerSecond?: number;
  onsiteApiCallsPerSecond?: number;
  publicApiCallsPerMonth?: number;
}

export class BasicAccount {
  public readonly customerId?: string;
  public readonly language?: string;
  public readonly isPaused?: boolean;
  public readonly isExpired?: boolean;
  public readonly expireDate?: string | null;

  constructor(
    public readonly siteId: number,
    public readonly name: string,
  ) {}
}

export enum AccountTypeCustomer {
  Invoice = 'Invoice',
  SelfService = 'SelfService',
  Free = 'Free',
}

/** Which shop system a customer runs, not who bills them. */
export enum EcommercePlatform {
  Centra = 'Centra',
  Shopify = 'Shopify',
  Shopware = 'Shopware',
}

export enum DeleteAccountAction {
  DeleteAccount = 'DeleteAccount',
  DeleteAccountAndGroup = 'DeleteAccountAndGroup',
}

export enum OperationType {
  Create,
  Update,
}

export interface StaticAccountData {
  groups?: Array<CustomerMock>;
  accountTypes?: Array<string>;
  planTypes?: Array<string>;
  currencies?: Array<Currency>;
  planSizes?: Array<string>;
  personalDataFields?: Array<string>;
  ipDbs?: Array<string>;
  planSizeValues?: {
    small: PlanSizeValue;
    medium: PlanSizeValue;
    big: PlanSizeValue;
  };
}

export interface PlanSizeValue {
  maxContacts: number;
  maxBatchRecordsPerMonth: number;
  maxPageviewsPerMonth: number;
}

export interface CustomProfileProperty {
  name: string;
  mapFrom: string;
  mapTo: string;
  id?: number;
  updatedTime?: string;
  createdTime?: string;
  isAccountIdentifier: boolean;
}

export interface UsersAdmin {
  userId?: string;
  createdAt?: number;
  email: string;
  fullName: string;
  isAdmin?: boolean;
  isEnabled: boolean;
  isSiteAdmin: boolean;
  language: string;
  lastLoggedInAt?: number;
  siteIds: number[];
  /** The role on each site. Empty while Identity keeps no roles. */
  sites?: SiteRole[];
  timeZoneId: string;
  hasEarlyAccess: boolean;
  earlyAccessFeatures: EarlyAccessFeatures[];
  hasMultiFactorAuthentication: boolean;
  multiFactorType: MultiFactorType;
  source?: SourceCoreModel;
  createdBy?: string;
  /** createdBy resolved to an email by the server. What the cell renders. Only on admin/users/search. */
  createdByEmail?: string;
  /** Names of the accounts the user belongs to, resolved server-side. Only on admin/users/search. */
  accountNames?: string[];
  /** accountNames joined for display, computed once when the page lands rather than per change detection. */
  accountNamesJoined?: string;
  isVerified?: boolean;
  /** The global roles the user has, such as CustomerSuccess, plus PlatformAdmin exactly when isAdmin is true. */
  roles?: string[];
}

export const SUPERADMIN_ROLE = 'PlatformAdmin';

/** One entry of Identity's catalogue of global roles a superadmin can give. */
export interface AssignableRole {
  name: string;
}
